import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import {
  ChevronLeft,
  Clock,
  Target,
  BookOpen,
  ShieldCheck,
  FileText,
  ListChecks,
  GraduationCap,
  Sparkles,
  Quote,
  Gavel,
  ClipboardCheck,
  Layers,
  Presentation,
  CheckSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCoursBySlug } from '../data/coursSessionCirculation';
import useSeo from '../hooks/useSeo';
import PresentationMode from '../components/cours-circulation/PresentationMode';
import ChecklistImprimable from '../components/cours-circulation/ChecklistImprimable';

const SectionTitle = ({ icon: Icon, children, accent }) => (
  <h2
    className="text-xl sm:text-2xl font-black text-foreground flex items-center gap-2 mb-3"
    style={{ borderLeft: `4px solid ${accent}`, paddingLeft: '0.75rem' }}
  >
    <Icon className="w-5 h-5" style={{ color: accent }} />
    {children}
  </h2>
);

export default function CoursSessionCirculationFiche() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cours = useMemo(() => getCoursBySlug(slug), [slug]);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);

  useSeo({
    title: cours
      ? `${cours.title} — Fiche séance CCS ECSR`
      : 'Fiche séance — Espace Pro ECSR',
    description: cours
      ? `${cours.subtitle} · Fiche séance type jury CCS de ${cours.duration}. Objectifs REMC, déroulé minuté, supports élève, évaluation finale, références juridiques.`
      : "Fiches séances type jury CCS ECSR pour la session de circulation.",
    path: `/enseignant/cours-circulation/${slug}`,
  });

  if (!cours) {
    return <Navigate to="/enseignant/cours-circulation" replace />;
  }

  const Icon = Icons[cours.icon] || Icons.BookOpen;
  const accent = cours.accent || '#cf2031';

  return (
    <div className="space-y-6" data-testid="cours-fiche-page">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane" className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-muted-foreground">
        <button
          onClick={() => navigate('/enseignant')}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          data-testid="fiche-breadcrumb-teacher"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Espace Pro ECSR
        </button>
        <span>›</span>
        <Link to="/enseignant/cours-circulation" className="hover:text-foreground transition-colors" data-testid="fiche-breadcrumb-hub">
          Cours session circulation
        </Link>
        <span>›</span>
        <span className="text-foreground font-semibold">Fiche n°{1}</span>
      </nav>

      {/* Hero / En-tête */}
      <section className="mc-card relative overflow-hidden" data-testid="fiche-hero">
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-30"
          style={{ background: accent }}
        />
        <div className="relative p-6 sm:p-10">
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-white"
              style={{ background: accent }}
            >
              <Icon className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                Fiche séance · CCS ECSR · {cours.level} · {cours.duration}
              </div>
              <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-foreground leading-tight">
                {cours.title}
              </h1>
              <p className="text-sm sm:text-lg italic mt-3 leading-relaxed" style={{ color: accent }}>
                « {cours.phraseCle} »
              </p>
              <p className="text-sm text-foreground/75 mt-2">
                {cours.subtitle} · {cours.format}
              </p>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-3">
                Source : {cours.source.label} ({cours.source.pages})
              </div>
            </div>
          </div>

          {/* Barre d'actions V2 : Présentation + Checklist + Impression */}
          <div
            className="mt-6 pt-5 border-t border-border flex flex-wrap items-center gap-2 sm:gap-3"
            data-testid="fiche-actions-v2"
          >
            <Button
              onClick={() => setPresentationOpen(true)}
              className="text-white"
              style={{ background: accent }}
              data-testid="open-presentation-mode"
            >
              <Presentation className="w-4 h-4 mr-1.5" />
              Mode présentation
            </Button>
            <Link to={`/enseignant/cours-circulation/${slug}/presenter`} data-testid="open-presentation-pro-link">
              <Button
                className="text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #cf2031 0%, #7c3aed 100%)' }}
                data-testid="open-presentation-pro"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Mode Pro · Studio interactif
                <span className="ml-1.5 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-white/20 rounded-md">NEW</span>
              </Button>
            </Link>
            <Button
              onClick={() => setChecklistOpen(true)}
              variant="outline"
              className="border-2"
              style={{ borderColor: accent, color: accent }}
              data-testid="open-checklist"
            >
              <CheckSquare className="w-4 h-4 mr-1.5" />
              Checklist imprimable
            </Button>
            <Button
              onClick={() => window.print()}
              variant="ghost"
              data-testid="print-fiche-complete"
            >
              <FileText className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Imprimer la fiche complète</span>
              <span className="sm:hidden">Imprimer</span>
            </Button>
          </div>
        </div>
      </section>

      {/* 1. Compétences REMC */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-remc">
        <SectionTitle icon={Target} accent={accent}>1. Compétences REMC ciblées</SectionTitle>
        <div className="space-y-3">
          {cours.remc.competences.map((c) => (
            <div key={c.code} className="rounded-xl bg-[hsl(var(--primary-soft))] p-3">
              <div className="font-black text-sm text-[hsl(var(--primary))]">{c.code} — {c.label}</div>
              <ul className="mt-1.5 space-y-0.5">
                {c.souscompetences.map((s, i) => (
                  <li key={i} className="text-xs text-foreground/80 flex gap-2">
                    <span className="text-[hsl(var(--primary))]">▸</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-foreground/70 italic">
          <strong>Matrice GDE :</strong> {cours.remc.matrice_gde}<br />
          <strong>Pédagogie :</strong> {cours.remc.ppo}
        </div>
      </section>

      {/* 2. Objectifs opérationnels */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-objectifs">
        <SectionTitle icon={Sparkles} accent={accent}>2. Objectifs opérationnels</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-950/30 p-3 border border-blue-200/50 dark:border-blue-900/40">
            <div className="text-[10px] font-black uppercase tracking-widest text-blue-700 dark:text-blue-300 mb-1.5">Savoir</div>
            <p className="text-xs text-foreground/85 leading-relaxed">{cours.objectifs.savoir}</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 p-3 border border-amber-200/50 dark:border-amber-900/40">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1.5">Savoir-faire</div>
            <p className="text-xs text-foreground/85 leading-relaxed">{cours.objectifs.savoirFaire}</p>
          </div>
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-3 border border-emerald-200/50 dark:border-emerald-900/40">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1.5">Savoir-être</div>
            <p className="text-xs text-foreground/85 leading-relaxed">{cours.objectifs.savoirEtre}</p>
          </div>
        </div>
      </section>

      {/* 3. Pré-requis */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-prerequis">
        <SectionTitle icon={ListChecks} accent={accent}>3. Pré-requis élève</SectionTitle>
        <ul className="space-y-2">
          {cours.prerequis.map((p, i) => (
            <li key={i} className="flex gap-2 text-sm text-foreground/85">
              <span style={{ color: accent }} className="font-black">▸</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 4. Déroulé minuté */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-deroule">
        <SectionTitle icon={Clock} accent={accent}>4. Déroulé minuté ({cours.duration})</SectionTitle>
        <ol className="space-y-4">
          {cours.deroule.map((d) => (
            <li key={d.etape} className="rounded-xl border border-border bg-card p-4" data-testid={`etape-${d.etape}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h3 className="font-black text-base text-foreground">{d.titre}</h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-bold" style={{ background: `${accent}20`, color: accent }}>
                    <Clock className="w-3 h-3" /> {d.duree}
                  </span>
                  <span className="text-muted-foreground font-bold">{d.role}</span>
                </div>
              </div>
              <p className="text-sm text-foreground/85 mb-3">{d.animation}</p>
              {d.questions_eleve?.length > 0 && (
                <div className="rounded-lg bg-[hsl(var(--primary-soft))] p-3 mb-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1.5">Questions à poser à l’élève</div>
                  <ul className="space-y-1">
                    {d.questions_eleve.map((q, i) => (
                      <li key={i} className="text-xs text-foreground/85 flex gap-2">
                        <Quote className="w-3 h-3 mt-0.5 flex-shrink-0 text-[hsl(var(--primary))]" />
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.attendu && (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-3 border border-emerald-200/40 dark:border-emerald-900/30">
                  <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1">Attendu / Réponse cible</div>
                  <p className="text-xs text-foreground/85">{d.attendu}</p>
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* 5. Contenus détaillés */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-contenus">
        <SectionTitle icon={Layers} accent={accent}>5. Contenus pédagogiques détaillés</SectionTitle>
        <div className="space-y-4">
          {cours.contenus.map((c) => (
            <article key={c.id} className="rounded-xl border border-border p-4 bg-card" data-testid={`contenu-${c.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h3 className="font-black text-base text-foreground">{c.titre}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.page_source}</span>
              </div>
              <ul className="space-y-1.5 mb-3">
                {c.points_cles.map((p, i) => (
                  <li key={i} className="text-sm text-foreground/85 flex gap-2">
                    <span style={{ color: accent }} className="font-black mt-0.5">●</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {c.schema && (
                <div className="text-xs italic text-muted-foreground border-l-2 pl-3" style={{ borderColor: accent }}>
                  <strong>Support visuel :</strong> {c.schema}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* 6. Supports élève */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-supports">
        <SectionTitle icon={BookOpen} accent={accent}>6. Supports élève à prévoir</SectionTitle>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {cours.supports.map((s, i) => (
            <li key={i} className="flex gap-2 text-sm text-foreground/85">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accent }} />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 7. Évaluation */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-evaluation">
        <SectionTitle icon={ClipboardCheck} accent={accent}>7. Évaluation finale ({cours.evaluation.duree})</SectionTitle>
        <p className="text-sm font-bold text-foreground mb-3">{cours.evaluation.type}</p>

        <div className="space-y-3">
          {cours.evaluation.questions.map((q, idx) => (
            <details key={idx} className="group rounded-xl border border-border bg-card p-3" data-testid={`qcm-${idx}`}>
              <summary className="cursor-pointer font-bold text-sm text-foreground flex justify-between items-start gap-3">
                <span><span style={{ color: accent }}>Q{idx + 1}.</span> {q.q}</span>
                <span className="text-xs text-muted-foreground group-open:hidden">+ voir</span>
                <span className="text-xs text-muted-foreground hidden group-open:inline">− replier</span>
              </summary>
              <ol className="mt-3 space-y-1 list-[upper-alpha] pl-5">
                {q.choix.map((ch, i) => (
                  <li
                    key={i}
                    className={`text-sm ${i === q.bonne ? 'font-black text-emerald-700 dark:text-emerald-300' : 'text-foreground/80'}`}
                  >
                    {ch}{i === q.bonne ? '  ✓' : ''}
                  </li>
                ))}
              </ol>
              <div className="mt-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2.5 border border-emerald-200/40 dark:border-emerald-900/30">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-0.5">Explication</div>
                <p className="text-xs text-foreground/85">{q.explication}</p>
              </div>
            </details>
          ))}
        </div>

        {/* Cas pratique */}
        <div className="mt-4 rounded-xl border-2 p-4" style={{ borderColor: accent }} data-testid="cas-pratique">
          <div className="text-[10px] font-black uppercase tracking-widest mb-1.5" style={{ color: accent }}>Cas pratique oral</div>
          <p className="text-sm text-foreground italic mb-3">« {cours.evaluation.cas_pratique.enonce} »</p>
          <details className="group">
            <summary className="cursor-pointer text-xs font-black uppercase tracking-widest text-muted-foreground">Voir la réponse attendue</summary>
            <p className="mt-2 text-sm text-foreground/85 leading-relaxed">{cours.evaluation.cas_pratique.reponse_attendue}</p>
          </details>
        </div>
      </section>

      {/* 8. Références juridiques */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-references">
        <SectionTitle icon={Gavel} accent={accent}>8. Références juridiques & normatives</SectionTitle>
        <ul className="space-y-2">
          {cours.references.map((r, i) => (
            <li key={i} className="rounded-lg bg-muted/40 p-3 text-sm">
              <div className="font-bold text-foreground">{r.titre}</div>
              <div className="text-xs text-foreground/75 mt-0.5">{r.detail}</div>
            </li>
          ))}
        </ul>
      </section>

      {/* 9. Conseils jury */}
      <section className="mc-card p-5 sm:p-6" data-testid="fiche-conseils-jury">
        <SectionTitle icon={GraduationCap} accent={accent}>9. Conseils jury CCS</SectionTitle>
        <ul className="space-y-2">
          {cours.conseils_jury.map((c, i) => (
            <li key={i} className="flex gap-2 text-sm text-foreground/85">
              <span className="font-black flex-shrink-0" style={{ color: accent }}>{i + 1}.</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Retour hub */}
      <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
        <Button variant="outline" onClick={() => navigate('/enseignant/cours-circulation')} data-testid="fiche-back-hub">
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour aux fiches
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setChecklistOpen(true)}
            className="border-2"
            style={{ borderColor: accent, color: accent }}
            data-testid="open-checklist-footer"
          >
            <CheckSquare className="w-4 h-4 mr-1.5" /> Checklist
          </Button>
          <Button
            onClick={() => setPresentationOpen(true)}
            className="text-white"
            style={{ background: accent }}
            data-testid="open-presentation-footer"
          >
            <Presentation className="w-4 h-4 mr-1.5" /> Lancer la présentation
          </Button>
        </div>
      </div>

      {/* Overlays V2 */}
      <PresentationMode
        cours={cours}
        open={presentationOpen}
        onClose={() => setPresentationOpen(false)}
      />
      <ChecklistImprimable
        cours={cours}
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
      />
    </div>
  );
}
